export default function VMware() {
  const vmwareServices = [
    {
      icon: 'ri-cloud-line',
      title: 'VMware Cloud Foundation',
      description: 'Complete VCF deployment, configuration, and management. Expert implementation of hybrid cloud infrastructure with automated lifecycle management.',
      features: ['VCF Architecture Design', 'Multi-Cloud Integration', 'Workload Domain Setup', 'SDDC Management']
    },
    {
      icon: 'ri-shield-line',
      title: 'VMware NSX',
      description: 'Advanced network virtualization and security solutions. Micro-segmentation, distributed firewall, and software-defined networking expertise.',
      features: ['NSX-T Deployment', 'Micro-Segmentation', 'Distributed Firewall', 'Network Automation']
    },
    {
      icon: 'ri-scales-3-line',
      title: 'Advanced Load Balancer',
      description: 'VMware Advanced Load Balancer (AVI) implementation, migration from legacy solutions, and optimization for modern applications.',
      features: ['AVI Deployment', 'F5 to AVI Migration', 'Application Analytics', 'Multi-Cloud LB']
    },
    {
      icon: 'ri-robot-line',
      title: 'VCF Automation',
      description: 'End-to-end automation for VCF operations using vRealize Suite, Terraform, and custom orchestration workflows.',
      features: ['Infrastructure as Code', 'vRealize Automation', 'API Integration', 'Custom Workflows']
    },
    {
      icon: 'ri-settings-3-line',
      title: 'VCF Operations',
      description: 'Comprehensive operational support including monitoring, troubleshooting, patching, and performance optimization for VCF environments.',
      features: ['Health Monitoring', 'Patch Management', 'Performance Tuning', 'Capacity Planning']
    },
    {
      icon: 'ri-restart-line',
      title: 'VMware Live Recovery',
      description: 'Disaster recovery and business continuity solutions with VMware Live Recovery for instant failover and data protection.',
      features: ['DR Planning', 'Instant Failover', 'RPO/RTO Optimization', 'Recovery Testing']
    },
    {
      icon: 'ri-arrow-left-right-line',
      title: 'HCX Migration',
      description: 'Seamless workload migration using VMware HCX. Zero-downtime migrations between on-premises and cloud environments.',
      features: ['Bulk Migration', 'Live vMotion', 'Network Extension', 'Cloud Onboarding']
    },
    {
      icon: 'ri-microsoft-line',
      title: 'Azure VMware Solution',
      description: 'Expert deployment and management of AVS environments. Native Azure integration with VMware workloads for hybrid cloud excellence.',
      features: ['AVS Deployment', 'Azure Integration', 'Hybrid Networking', 'Cost Optimization']
    }
  ];

  return (
    <section id="vmware" className="py-24 bg-gradient-to-br from-slate-50 to-cyan-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center w-16 h-16 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-2xl mb-6">
            <i className="ri-cloud-line text-3xl text-white"></i>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Broadcom VMware Services
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Comprehensive VMware expertise spanning Cloud Foundation, NSX, Advanced Load Balancer, 
            and hybrid cloud solutions. Transform your infrastructure with our certified VMware specialists.
          </p>
        </div>

        {/* Services Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 mb-16">
          {vmwareServices.map((service, index) => (
            <div
              key={index}
              className="bg-white rounded-2xl p-8 shadow-lg hover:shadow-2xl transition-all duration-300 hover:-translate-y-2 border border-gray-100 group"
            >
              <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-teal-500 to-cyan-600 rounded-xl mb-6 group-hover:scale-110 transition-transform duration-300">
                <i className={`${service.icon} text-2xl text-white`}></i>
              </div>
              <h3 className="text-xl font-bold text-gray-900 mb-3">
                {service.title}
              </h3>
              <p className="text-gray-600 mb-6 text-sm leading-relaxed">
                {service.description}
              </p>
              <ul className="space-y-2">
                {service.features.map((feature, idx) => (
                  <li key={idx} className="flex items-start text-sm">
                    <i className="ri-check-line text-teal-600 mt-0.5 mr-2 flex-shrink-0"></i>
                    <span className="text-gray-700">{feature}</span>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Certifications & Partnership Badges */}
        <div className="mb-16">
          <h3 className="text-2xl font-bold text-gray-900 mb-3 text-center">
            Certifications &amp; Partnerships
          </h3>
          <p className="text-gray-600 text-center mb-10 max-w-2xl mx-auto text-sm">
            Our team holds industry-leading VMware certifications and maintains strategic partnerships with Broadcom, ensuring top-tier expertise for every engagement.
          </p>

          {/* Partner Tier */}
          <div className="flex flex-col items-center mb-12">
            <div className="bg-white rounded-2xl shadow-lg border border-gray-100 px-10 py-8 flex flex-col items-center max-w-md w-full">
              <div className="flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-br from-teal-500 to-cyan-600 mb-4">
                <i className="ri-award-fill text-4xl text-white"></i>
              </div>
              <span className="text-xs font-semibold tracking-widest uppercase text-teal-600 mb-1">Broadcom</span>
              <span className="text-xl font-bold text-gray-900 mb-2">VMware Certified Partner</span>
              <span className="text-gray-500 text-sm text-center">Authorized to deliver VMware solutions, migrations, and managed services across all product lines</span>
            </div>
          </div>

          {/* Individual Certifications */}
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 mb-10">
            {[
              { abbr: 'VCP-DCV', label: 'Data Center Virtualization', icon: 'ri-server-line' },
              { abbr: 'VCP-NV', label: 'Network Virtualization', icon: 'ri-shield-line' },
              { abbr: 'VCAP-DCV', label: 'Advanced DC Design', icon: 'ri-layout-masonry-line' },
              { abbr: 'VCP-CMA', label: 'Cloud Management', icon: 'ri-cloud-line' },
              { abbr: 'VCP-DTM', label: 'Desktop & Mobility', icon: 'ri-computer-line' },
              { abbr: 'VCAP-NV', label: 'Advanced Network Design', icon: 'ri-route-line' },
            ].map((cert, idx) => (
              <div
                key={idx}
                className="bg-white rounded-xl border border-gray-100 shadow-sm hover:shadow-lg transition-all duration-300 p-5 flex flex-col items-center text-center group hover:-translate-y-1"
              >
                <div className="flex items-center justify-center w-12 h-12 rounded-lg bg-teal-50 group-hover:bg-gradient-to-br group-hover:from-teal-500 group-hover:to-cyan-600 transition-all duration-300 mb-3">
                  <i className={`${cert.icon} text-xl text-teal-600 group-hover:text-white transition-colors duration-300`}></i>
                </div>
                <span className="text-sm font-bold text-gray-900 mb-1">{cert.abbr}</span>
                <span className="text-xs text-gray-500 leading-tight">{cert.label}</span>
              </div>
            ))}
          </div>

          {/* Technology Specialization Badges */}
          <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8">
            <h4 className="text-lg font-bold text-gray-900 mb-6 text-center">Technology Specializations</h4>
            <div className="flex flex-wrap justify-center gap-3">
              {[
                { label: 'VMware Cloud Foundation', icon: 'ri-cloud-line' },
                { label: 'NSX Networking & Security', icon: 'ri-shield-check-line' },
                { label: 'AVI Load Balancer', icon: 'ri-scales-3-line' },
                { label: 'HCX Migration', icon: 'ri-arrow-left-right-line' },
                { label: 'Azure VMware Solution', icon: 'ri-microsoft-line' },
                { label: 'vRealize Automation', icon: 'ri-robot-line' },
                { label: 'VMware Live Recovery', icon: 'ri-restart-line' },
                { label: 'vSphere Administration', icon: 'ri-settings-3-line' },
                { label: 'Tanzu Kubernetes', icon: 'ri-code-box-line' },
                { label: 'vSAN Storage', icon: 'ri-database-2-line' },
              ].map((spec, idx) => (
                <div
                  key={idx}
                  className="inline-flex items-center gap-2 px-4 py-2.5 bg-gradient-to-r from-teal-50 to-cyan-50 border border-teal-200/60 rounded-full hover:from-teal-100 hover:to-cyan-100 hover:border-teal-300 transition-all duration-300 cursor-default"
                >
                  <div className="flex items-center justify-center w-5 h-5">
                    <i className={`${spec.icon} text-sm text-teal-600`}></i>
                  </div>
                  <span className="text-sm font-medium text-gray-800 whitespace-nowrap">{spec.label}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Key Capabilities */}
        <div className="bg-white rounded-3xl p-10 shadow-xl border border-gray-100">
          <h3 className="text-2xl font-bold text-gray-900 mb-8 text-center">
            Our VMware Expertise
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-teal-100 rounded-xl mx-auto mb-4">
                <i className="ri-medal-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Certified Experts</h4>
              <p className="text-gray-600 text-sm">
                VMware Certified Professionals with deep expertise in VCF, NSX, and cloud solutions
              </p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-cyan-100 rounded-xl mx-auto mb-4">
                <i className="ri-rocket-line text-2xl text-cyan-600"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">End-to-End Solutions</h4>
              <p className="text-gray-600 text-sm">
                From design and deployment to migration, automation, and ongoing operations
              </p>
            </div>
            <div className="text-center">
              <div className="flex items-center justify-center w-12 h-12 bg-teal-100 rounded-xl mx-auto mb-4">
                <i className="ri-time-line text-2xl text-teal-600"></i>
              </div>
              <h4 className="font-bold text-gray-900 mb-2">Proven Track Record</h4>
              <p className="text-gray-600 text-sm">
                Successfully delivered complex VMware projects across enterprise environments
              </p>
            </div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <a
            href="#contact"
            className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-teal-600 to-cyan-600 text-white font-semibold rounded-xl hover:from-teal-700 hover:to-cyan-700 transition-all duration-300 shadow-lg hover:shadow-xl whitespace-nowrap cursor-pointer"
          >
            Discuss Your VMware Project
            <i className="ri-arrow-right-line ml-2"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
